<script setup lang="ts">
import { ref, computed } from 'vue'
import { Shield } from '@lucide/vue'
import { useAuthStore } from '@/stores/auth'
import { useConsentStatus, useReConsent } from '@/api/queries/useConsent'
import BaseModal from '@/components/shared/BaseModal.vue'
import BaseButton from '@/components/shared/BaseButton.vue'
import BaseAlert from '@/components/shared/BaseAlert.vue'
import ConsentCheckboxes from '@/components/auth/ConsentCheckboxes.vue'
import type { ConsentInput } from '@/types'

const authStore = useAuthStore()
const { data: consentStatus } = useConsentStatus()
const reConsentMutation = useReConsent()

const consents = ref<ConsentInput[]>([])

const requiresReConsent = computed(() => consentStatus.value?.requires_re_consent ?? false)
const mutationError = computed(() => {
  const err = reConsentMutation.error as { response?: { data?: { error?: { message?: string } } } } | null
  return err?.response?.data?.error?.message ?? null
})

const consentsValid = computed(() => {
  const role = authStore.role
  if (role === 'candidate') return consents.value.every(c => c.accepted)
  return consents.value.find(c => c.type === 'tos_privacy')?.accepted ?? false
})

async function submit() {
  if (!consentsValid.value) return
  try {
    await reConsentMutation.mutateAsync(consents.value)
  } catch { /* error shown via mutationError */ }
}
</script>

<template>
  <BaseModal
    :show="requiresReConsent"
    title="Perbarui Persetujuan"
    size="md"
    :close-on-overlay="false"
    persistent
  >
    <template #header>
      <div class="flex items-center gap-3">
        <div class="size-9 rounded-button bg-primary/10 flex items-center justify-center shrink-0">
          <Shield class="size-5 text-primary" />
        </div>
        <div>
          <h3 class="heading-card">
            Perbarui Persetujuan
          </h3>
          <p class="text-xs text-text-muted font-medium">
            Kebijakan kami telah diperbarui
          </p>
        </div>
      </div>
    </template>

    <div class="space-y-5">
      <p class="text-sm text-text-muted font-medium leading-relaxed">
        Kami telah memperbarui Kebijakan Privasi dan Syarat Penggunaan kami. Untuk melanjutkan menggunakan
        Sora Recruit, mohon tinjau dan setujui kebijakan terbaru di bawah ini.
      </p>

      <ConsentCheckboxes
        :role="authStore.role"
        @update:consents="c => (consents = c)"
      />

      <BaseAlert
        v-if="mutationError"
        type="error"
        :message="mutationError"
      />
    </div>

    <template #footer>
      <BaseButton
        variant="primary"
        block
        :loading="reConsentMutation.isPending.value"
        :disabled="!consentsValid"
        @click="submit"
      >
        Simpan Persetujuan
      </BaseButton>
    </template>
  </BaseModal>
</template>

